import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  authorizeUserPhy,
  authorizeUserQrCode,
  authorizeUserPolling,
  abortPolling,
} from "../../actions/authorizeActions";
import { arrayBufferToBase64, getSecretKey } from "../../utils/encryptHash";
import { useErrorContext } from "../../_shared/context/ErrorContext";
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import {
  LoginScreen,
  OTP_EXPIRATION_IN_SECONDS,
  QR_CODE_EXPIRATION_TIMESTAMP,
  QR_CODE_SCOPE,
  QrCodeValueType,
} from "../types";
import { PasswordResetScreen } from "@delorean/forgetPassword";
import {
  cancelLoginProcedure,
  setScreen,
} from "@delorean/actions/loginActions";
import { LocationType } from "../screens/NotificationScreen";
import * as loginRoutes from '../../_shared/routing'

export const useCodeVerification = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cookies] = useCookies(["locale"]);
  const [OTP, setOTP] = useState("");
  const { error, loading } = useSelector(
    (state: RootStateOrAny) => state.authorize
  );
  const { loading: loadingLoginForQr } = useSelector(
    (state: RootStateOrAny) => state.login
  );
  const mfaData = useSelector((state: RootStateOrAny) => state.login.mfaData);
  const loginData = useSelector((state: RootStateOrAny) => state.login);
  const operationId = useSelector(
    (state: RootStateOrAny) => state.login.operationId
  );
  const token_serialNumber = mfaData.challenge?.serialNumber || "";
  const token_userSeed = mfaData.challenge?.userSeed || "";
  const { showError } = useErrorContext();
  const { t } = useTranslation();

  const qrCodeValue = useMemo(() => {
    return <QrCodeValueType>{
      scope: QR_CODE_SCOPE,
      appData: loginData.mfaData.appData || "",
      idOperation: loginData.operationId || "",
      clientCode: loginData.userCode || "",
      contractCode: loginData.contractCode || "",
      otpExpirationInSeconds: OTP_EXPIRATION_IN_SECONDS,
      qrCodeExpirationTimestamp:
        Math.floor(Date.now() / 1000) + QR_CODE_EXPIRATION_TIMESTAMP * 60,
    };
  }, [
    loginData.mfaData.appData,
    loginData.operationId,
    loginData.userCode,
    loginData.contractCode,
  ]);

  const setCurrentScreen = useCallback(
    (screenType: LoginScreen | PasswordResetScreen) => {
      dispatch(setScreen(screenType));
    },
    [dispatch]
  );
  const getEncryptHashOTP = async () => {
    try {
      const hashOtp = {
        hash: mfaData.challenge.hashCode,
        OTP,
      };
      // Convert the hashOtp object to a JSON string
      const hashOtpString = JSON.stringify(hashOtp);
      // Get the secret key
      const secretKey = await getSecretKey(mfaData.challenge.cipherKey);
      const base64ToUint8Array = (base64: string): Uint8Array => {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };
      const iv = base64ToUint8Array(mfaData.challenge.initializationVector);
      // Encrypt the data
      const encryptedBuffer = await window.crypto.subtle.encrypt(
        { name: "AES-CBC", iv: iv },
        secretKey,
        new TextEncoder().encode(hashOtpString)
      );

      // Convert the encrypted data to a Base64 string
      const encryptedHash = arrayBufferToBase64(encryptedBuffer);

      return encryptedHash;
    } catch (error) {
      console.error("Encryption error:", error);
    }
  };
  useEffect(() => {
    if (error) {
      switch (error) {
        default:
          showError(t("errors.messageTwo"));
      }
    }
  }, [error]);

  const submit = useCallback(
    async (e?: FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      const encryptedHashOtp = (await getEncryptHashOTP()) ?? "";
      const lang = cookies?.locale || "it"; // Default to Italian if no cookie is set
      dispatch(
        authorizeUserPhy(OTP, encryptedHashOtp, operationId, mfaData, lang)
      );
    },
    [OTP, operationId, mfaData]
  );

  const qrCodeSubmit = useCallback(
    async (e?: FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      const lang = cookies?.locale || "it"; // Default to Italian if no cookie is set
      dispatch(authorizeUserQrCode(OTP, operationId, mfaData, lang));
    },
    [OTP, operationId, mfaData]
  );

  const submitPolling = useCallback(
    async (e?: FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      const lang = cookies?.locale || "it"; // Default to Italian if no cookie is set
      dispatch(authorizeUserPolling(operationId, mfaData, lang));
    },
    [OTP, operationId, mfaData]
  );

  const rollbackToLogin = (location : LocationType) => {
    // Abortisci il polling quando si cancella la procedura di login
    dispatch(abortPolling());
    if(location === LocationType.LOGIN){
      dispatch(cancelLoginProcedure());
    } else {
           navigate(loginRoutes.login, { state: { resetLogin: true } });
    }
  
  };
  return {
    OTP,
    setOTP,
    loading,
    loadingLoginForQr,
    submit,
    operationCode: operationId,
    token_serialNumber,
    token_userSeed,
    error,
    qrCodeValue,
    setCurrentScreen,
    rollbackToLogin,
    qrCodeSubmit,
    submitPolling,
  };
};
